import React, { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

import {
  Chip,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import Grid from "@mui/material/Grid2";

import { AudienceSkeleton } from "./AudienceSkeleton";
import { PageContainer } from "@/components/containers";
import {
  ActionButtons,
  AudienceSize,
  BuildModeSelect,
  HelpBox,
  TitleText,
  LocationSelection,
  SelectionStep,
} from "@/components/atoms";
import { AudienceMiniStats, StandardBuilder } from "@/components/molecules";
import { BuilderProvider, useAudienceActions } from "@/context";
import {
  useAudience,
  useAudienceDialogs,
  useAudiencePageState,
  useAudienceWorkflow,
  useLocations,
  useVariables,
} from "@/hooks";
import {
  AudiencePageActionId,
  AudiencePageRules,
  audiencePageActions,
  checkPermission,
  checkVariables,
  createBuildDefinition,
  hasVariables,
} from "@/utils";
import { ActionType, GeoFenceLocation } from "@/types";

const BUILD_ACTIONS: ActionType[] = ['build'];
const ACTION_ERRORS: AudiencePageRules["actionErrors"] = { BuildError: ['build'], InsightError: ['insight'] };
const PAGE_ACTIONS: AudiencePageActionId[] = ['delete', 'share', 'unshare', 'clone', 'build', 'insight', 'export'];


const AudiencePage: React.FC = () => {

  const audienceActions = useAudienceActions();
  const { user } = useAuth0();

  // Fetch Page Data
  let { audienceId } = useParams<{ audienceId: string }>();
  audienceId = audienceId === "new" ? undefined : audienceId;
  const {
    audience, loading: audLoad, error: audErr,
  } = useAudience(audienceId, "build");
  const {
    variables, loading: varLoad, error: varErr,
  } = useVariables();
  const {
    locations, loading: locLoad, error: locErr,
  } = useLocations();

  const workflow = useAudienceWorkflow(audienceId, audience);
  const { action, isRunning } = workflow;
  const dialogs = useAudienceDialogs(audience);

  const isVarInvalid = !!(audience && variables) && !checkVariables(audience, variables);
  const rules = useMemo(() => ({
    readyToBuild: hasVariables(audience) && !isVarInvalid,
    lockWhileBusy: true,
    buildActions: BUILD_ACTIONS,
    actionErrors: ACTION_ERRORS,
    extraErrors: {
      DataUnavailable: varErr || locErr,
      InvalidVariables: isVarInvalid,
    },
  }), [audience, isVarInvalid, varErr, locErr]);

  const pageState = useAudiencePageState({
    workflow,
    audience,
    isNew: audienceId === undefined,
    loading: audLoad || varLoad || locLoad,
    loadError: !!(audErr || varErr || locErr),
    audienceError: audErr,
    definition: createBuildDefinition(audience),
    rules,
  });

  const handleChangeMode = (option: {value: string, label: string}) => {
    audienceActions.setBuildMode(option.value);
  }

  const handleSelectLocation = (locations: GeoFenceLocation[]) => {
    audienceActions.setGeoFence(locations);
  };

  const pageActions = useMemo(
    () => audiencePageActions(pageState, workflow, dialogs, PAGE_ACTIONS),
    [pageState, workflow, dialogs]
  );

  return (
    <PageContainer
      actions={<ActionButtons actions={pageActions} />}
      errors={pageState.errors}
      loading={isRunning()}
      loadingText={action.message}
    >
      {pageState.isLoading ? (
        <AudienceSkeleton />
      ) : (
        <Grid container spacing={4} sx={{ pb: 4 }}>
          {audience && variables && locations && (
            <>
              {/* Audience Name and Summary */}
              <Grid
                size={12}
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="h6">
                    {audience.audience_name || "New Audience"}
                  </Typography>
                  {audience.project_name && (
                    <Chip size="small" label={audience.project_name} />
                  )}
                  {audienceId && !pageState.isLocked && (
                    <HelpBox title="Edit Audience/Project Name">
                      <IconButton
                        size="small"
                        onClick={dialogs.promptRename}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </HelpBox>
                  )}
                </Stack>
                {audience.audience_size && (
                  <AudienceMiniStats audienceSize={audience.audience_size[0]} />
                )}
              </Grid>

              <BuilderProvider audience={audience} disabled={pageState.isLocked}>
                {user && checkPermission(user, "ai") ? (
                  <SelectionStep audience={audience} />
                ) : (
                  <StandardBuilder audience={audience} />
                )}
              </BuilderProvider>


              {/*<Grid size={12}>*/}
              {/*  <HelpText*/}
              {/*    text="Audience Settings"*/}
              {/*    helpText="Additional settings to configure your audience further"*/}
              {/*    variant="body1"*/}
              {/*  />*/}
              {/*</Grid>*/}

              <Grid size={12}>
                <BuildModeSelect
                  value={audience.build_mode}
                  onChange={handleChangeMode}
                  disabled={pageState.isLocked}
                />
              </Grid>

              <Grid size={12}>
                <LocationSelection
                  locations={locations}
                  selection={audience.geo_fence}
                  onSelect={handleSelectLocation}
                  disabled={pageState.isLocked}
                />
              </Grid>

              {audience.audience_size && (
                <>
                  <Grid size={12}>
                    <Stack spacing={2}>
                      <TitleText
                        text="Audience Size"
                        subtext="How many postcodes match your audience definition and therefore how many households and people you are likely to reach."
                        variant="body1"
                        spacing={0.5}
                      />
                      <AudienceSize data={audience.audience_size[0]} />
                    </Stack>
                  </Grid>
                </>
              )}
            </>
          )}
        </Grid>
      )}
    </PageContainer>
  );
};

export default AudiencePage;
